import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { atsCompare, atsMake } from '../../lib/api';
import type { AtsAnswer, AtsModelInfo, AtsRecord } from '../../lib/api';
import { Btn, C, Card, MatchMeter, MODEL_META, Modal, ModelChip, Pill, ScoreRing, VERDICT, rupees } from './ui';

// Step ② Match report: the ATS score now, eligibility, the job's skills
// found and missing, how each project fits, and projects that would help
// ("Can I add this project?"). Then Make — or Compare models first.

const ANSWERS: [AtsAnswer, string, string][] = [
  ['done', '✅', 'Has done it'],
  ['build', '🛠️', 'Will build it'],
  ['skip', '⏭️', 'Skip'],
];

const CompareModal: React.FC<{
  open: boolean; onClose: () => void; models: AtsModelInfo[]; busy: boolean; onStart: (ids: string[]) => void;
}> = ({ open, onClose, models, busy, onStart }) => {
  const [picked, setPicked] = useState<Set<string>>(() => new Set(['claude-sonnet-5-5', 'claude-opus-5-5']));
  const chosen = models.filter(m => picked.has(m.id));
  const total = chosen.reduce((s, m) => s + m.make.inr, 0);
  const toggle = (id: string) => setPicked(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else if (n.size < 4) n.add(id);
    return n;
  });
  return (
    <Modal open={open} onClose={onClose} title="⚖ Compare models"
      sub="The same check and the same job — each model writes its own version. You pick the best one."
      footer={
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <p className="text-xs text-gray-600 flex-1">
            {chosen.length < 2 ? 'Pick at least 2 models.' : <>This test costs about <b className="text-gray-900">{rupees(total)}</b> ({chosen.length} versions).</>}
          </p>
          <Btn kind="dark" size="lg" disabled={chosen.length < 2 || busy} onClick={() => onStart(chosen.map(m => m.id))}>
            {busy ? 'Starting…' : `Start comparing ${chosen.length} models`}
          </Btn>
        </div>
      }>
      <div className="grid sm:grid-cols-2 gap-3">
        {models.map(m => {
          const on = picked.has(m.id);
          const meta = MODEL_META[m.id];
          return (
            <button key={m.id} onClick={() => toggle(m.id)} data-compare-model={m.id}
              className={`text-left rounded-2xl p-4 border-2 transition-all ${on ? 'bg-white' : 'border-gray-100 bg-gray-50/50 hover:border-purple-200'}`}
              style={on ? { borderColor: meta?.color, boxShadow: `0 10px 24px -16px ${meta?.color}` } : {}}>
              <div className="flex items-center justify-between gap-2">
                <p className="font-black text-sm" style={{ color: meta?.color }}>{meta?.icon} {m.name}</p>
                <span className={`w-5 h-5 rounded-md border-2 flex items-center justify-center text-[11px] font-black ${on ? 'text-white' : 'border-gray-300'}`}
                  style={on ? { background: meta?.color, borderColor: meta?.color } : {}}>{on ? '✓' : ''}</span>
              </div>
              <p className="text-xs text-gray-500 mt-1">{m.blurb}</p>
              <div className="flex items-center gap-3 mt-2 text-[11px] font-bold text-gray-700">
                <span>~{rupees(m.make.inr)}{m.make.estimated ? ' est.' : ''}</span>
                {m.make.sec != null && <span className="text-gray-400">· ~{Math.round(m.make.sec)}s</span>}
                {m.wins > 0 && <span className="text-amber-600">· 🏆 {m.wins}</span>}
              </div>
            </button>
          );
        })}
      </div>
      <p className="text-[11px] text-gray-400 mt-4">Each version goes through the same fact check. The results also go to the Model Lab, so you can see which model is best over time.</p>
    </Modal>
  );
};

const MatchReport: React.FC<{
  rec: AtsRecord; pw: string; models: AtsModelInfo[]; onStarted: () => void;
}> = ({ rec, pw, models, onStarted }) => {
  const check = rec.check!;
  const [answers, setAnswers] = useState<Record<string, AtsAnswer>>(() => rec.choices?.answers ?? {});
  const [anyway, setAnyway] = useState(!!rec.choices?.makeAnyway);
  const [sending, setSending] = useState(false);
  const [comparing, setComparing] = useState(false);
  const verdict = VERDICT[check.eligibility.verdict];
  const notEligible = check.eligibility.verdict === 'not_eligible';
  const found = rec.keywords.filter(k => k.before);
  const missing = rec.keywords.filter(k => !k.before);
  const mustMissing = missing.filter(k => k.importance === 'must').length;
  const adding = check.suggestions.filter(s => ['done', 'build'].includes(answers[s.id] ?? 'skip'));
  const gainFromAdds = adding.reduce((s, x) => s + x.gain, 0);
  const writer = models.find(m => m.id === rec.models.make);

  const make = async () => {
    setSending(true);
    try { await atsMake(pw, rec._id, answers, anyway); onStarted(); }
    catch (err: any) { toast.error(err.message); }
    finally { setSending(false); }
  };
  const compare = async (ids: string[]) => {
    setSending(true);
    try { await atsCompare(pw, rec._id, ids, answers, anyway); setComparing(false); onStarted(); }
    catch (err: any) { toast.error(err.message); }
    finally { setSending(false); }
  };

  return (
    <div className="space-y-5 ats-fade">
      {/* The headline result */}
      <div className="relative overflow-hidden rounded-3xl p-6 sm:p-7 text-white ats-card" style={{ background: `linear-gradient(135deg, ${C.purple} 0%, #3d2f5f 60%, ${C.purple2} 100%)` }}>
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full ats-glow" style={{ background: 'radial-gradient(circle, rgba(240,180,41,.35), transparent 65%)' }} />
        <div className="relative flex flex-col md:flex-row md:items-center gap-6">
          <div className="bg-white rounded-full p-2 self-start md:self-auto shadow-xl">
            <ScoreRing value={rec.scoreBefore} size={128} stroke={11} label="ATS now" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-white/70 mb-2 truncate">
              <span className="text-white font-black">{rec.candidateName || 'Candidate'}</span> · {rec.jobTitle}{rec.company ? ` · ${rec.company}` : ''}
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Pill bg={verdict.bg} color={verdict.color} className="text-xs px-3 py-1.5">{verdict.icon} {verdict.label}</Pill>
              <span className="text-xs font-semibold text-white/60">{found.length} of {rec.keywords.length} job keywords found{mustMissing ? ` · ${mustMissing} must-have${mustMissing === 1 ? '' : 's'} missing` : ''}</span>
            </div>
            <p className="text-lg sm:text-xl font-extrabold mt-3 leading-snug">{check.overview || check.eligibility.summary || 'Here is how this resume matches the job.'}</p>
            <p className="text-xs text-white/50 mt-2">🔒 Education and facts stay exactly as in the resume. Only the wording changes.</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <Card title="Skills the job wants" icon="🎯" right={<span className="text-[11px] font-bold text-gray-400">{found.length}/{rec.keywords.length}</span>}>
          <p className="text-[11px] font-extrabold text-green-700 uppercase tracking-wider mb-2">In the resume</p>
          <div className="flex flex-wrap gap-1.5">
            {found.length ? found.map(k => <Pill key={k.term} bg="#dcfce7" color="#166534">✓ {k.term}</Pill>) : <p className="text-xs text-gray-400">None yet</p>}
          </div>
          <p className="text-[11px] font-extrabold text-gray-500 uppercase tracking-wider mt-4 mb-2">Missing</p>
          <div className="flex flex-wrap gap-1.5">
            {missing.length ? missing.map(k => (
              <Pill key={k.term} bg={k.importance === 'must' ? '#fef2f2' : '#f3f4f6'} color={k.importance === 'must' ? '#b91c1c' : '#6b7280'}>
                {k.term}{k.importance === 'must' && ' ★'}
              </Pill>
            )) : <p className="text-xs text-green-700 font-semibold">Nothing missing 🎉</p>}
          </div>
          {mustMissing > 0 && <p className="text-[11px] text-gray-400 mt-3">★ the job must have it. The AI never adds a skill the candidate doesn't have — add it only if it's true.</p>}
        </Card>

        <Card title="Eligibility" icon="🎓" right={<Pill bg={verdict.bg} color={verdict.color}>{verdict.label}</Pill>}>
          {check.eligibility.items.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {check.eligibility.items.map((it, i) => (
                <div key={i} className="flex gap-3 py-2.5 first:pt-0 last:pb-0">
                  <span className={`w-6 h-6 rounded-full shrink-0 flex items-center justify-center text-xs font-black ${it.status === 'ok' ? 'bg-green-100 text-green-700' : it.status === 'fail' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                    {it.status === 'ok' ? '✓' : it.status === 'fail' ? '✗' : '?'}
                  </span>
                  <div className="min-w-0 flex-1 text-sm">
                    <p className="font-extrabold text-gray-900">{it.area}</p>
                    <div className="grid sm:grid-cols-2 gap-x-4 text-xs mt-0.5">
                      <p className="text-gray-600"><span className="text-gray-400">Job asks: </span>{it.job}</p>
                      <p className="text-gray-600"><span className="text-gray-400">Resume: </span>{it.resume}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-gray-500">The job doesn't state any education, batch, marks or experience rule.</p>}
          {check.eligibility.summary && check.eligibility.items.length > 0 && <p className="text-xs text-gray-500 mt-3 pt-3 border-t border-gray-100">{check.eligibility.summary}</p>}
        </Card>
      </div>

      {check.original.projects.length > 0 && (
        <Card title="Projects in the resume" icon="📁">
          <div className="grid md:grid-cols-2 gap-3">
            {check.original.projects.map(p => {
              const m = check.projectMatch.find(x => x.id === p.id);
              return (
                <div key={p.id} className="rounded-2xl border border-gray-100 bg-gray-50/60 p-3.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="font-extrabold text-sm text-gray-900">{p.name}</p>
                    {m && <MatchMeter match={m.match} />}
                  </div>
                  {m?.reason && <p className="text-xs text-gray-500 mt-1">{m.reason}</p>}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {check.suggestions.length > 0 && (
        <div className="space-y-3">
          <p className="flex items-center gap-2 text-[15px] font-extrabold text-gray-900 px-1">💡 Projects that would get more calls</p>
          {check.suggestions.map(s => {
            const a = answers[s.id] ?? 'skip';
            return (
              <Card key={s.id} className={a !== 'skip' ? 'ring-2 ring-[#F0B429]/60' : ''}>
                <div className="flex flex-col md:flex-row gap-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-black text-lg text-gray-900">{s.title}</p>
                      {s.gain > 0 && <Pill bg="#dcfce7" color="#166534">📈 +{s.gain}% ATS score</Pill>}
                    </div>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {s.tech.map(t => <Pill key={t} bg={s.adds.includes(t) ? '#fef3c7' : '#f5f3ff'} color={s.adds.includes(t) ? '#92400e' : '#5b21b6'}>{t}</Pill>)}
                    </div>
                    <p className="text-sm text-gray-700 mt-3">📞 {s.why}</p>
                    <details className="mt-2 group">
                      <summary className="text-xs font-bold text-purple-700 cursor-pointer select-none list-none">▸ See the resume lines</summary>
                      <ul className="mt-2 space-y-1">{s.bullets.map((b, i) => <li key={i} className="text-sm text-gray-600">• {b}</li>)}</ul>
                    </details>
                  </div>
                  <div className="md:w-60 shrink-0">
                    <p className="text-sm font-extrabold text-gray-900 mb-2">Can I add this project?</p>
                    <div className="grid grid-cols-3 md:grid-cols-1 gap-1.5">
                      {ANSWERS.map(([k, icon, label]) => (
                        <button key={k} onClick={() => setAnswers(x => ({ ...x, [s.id]: k }))}
                          className="flex items-center justify-center md:justify-start gap-2 px-3 py-2 rounded-xl text-xs font-extrabold border-2 transition-all"
                          style={a === k ? { background: C.purple, color: '#fff', borderColor: C.purple } : { background: '#fff', color: '#4b5563', borderColor: '#eceaf2' }}>
                          <span>{icon}</span>{label}
                        </button>
                      ))}
                    </div>
                    {a === 'build' && <p className="text-[11px] text-gray-500 mt-2">You'll get a step-by-step build plan. Finish it before applying — interviewers ask about every project.</p>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {notEligible && (
        <div className="rounded-3xl p-5 border-2 flex flex-col sm:flex-row sm:items-center gap-4" style={{ borderColor: '#fecaca', background: '#fff5f5' }}>
          <span className="text-3xl">⛔</span>
          <div className="flex-1">
            <p className="font-extrabold text-red-800">Not eligible for this job</p>
            <p className="text-sm text-red-700 mt-0.5">{check.eligibility.summary || "The resume doesn't meet the job's education or other must-have rules."}</p>
          </div>
          <label className="flex items-center gap-2 text-sm font-extrabold text-red-800 cursor-pointer bg-white rounded-xl px-3 py-2 border border-red-200">
            <input type="checkbox" checked={anyway} onChange={e => setAnyway(e.target.checked)} className="w-4 h-4 accent-red-600" /> Make it anyway
          </label>
        </div>
      )}

      {/* The action bar stays in view */}
      <div className="sticky bottom-0 z-20 -mx-1 px-1 pb-1">
        <div className="rounded-3xl border border-gray-200 bg-white/95 backdrop-blur ats-card px-4 sm:px-5 py-3.5 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1 min-w-0 text-xs text-gray-600">
            <div className="flex flex-wrap items-center gap-2">
              <ModelChip id={rec.models.make} prefix="Writes with " />
              {writer && <span className="font-bold text-gray-800">~{rupees(writer.make.inr)}</span>}
            </div>
            <p className="mt-1">
              {adding.length ? <>Adding <b>{adding.length}</b> project{adding.length === 1 ? '' : 's'}{gainFromAdds > 0 ? <> · up to <b className="text-green-700">+{gainFromAdds}%</b></> : ''} · </> : ''}
              Only the wording changes. Takes about a minute.
            </p>
          </div>
          <div className="flex gap-2">
            <Btn kind="ghost" size="lg" onClick={() => setComparing(true)} disabled={sending || (notEligible && !anyway) || models.length === 0}>⚖ Compare models</Btn>
            <Btn kind="gold" size="lg" onClick={make} disabled={sending || (notEligible && !anyway)}>{sending ? 'Starting…' : '✨ Make my ATS resume'}</Btn>
          </div>
        </div>
      </div>

      <CompareModal open={comparing} onClose={() => setComparing(false)} models={models} busy={sending} onStart={compare} />
    </div>
  );
};

export default MatchReport;
